import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Trash2, X, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_COLOR = {
  National: 'bg-red-100 text-red-700',
  Regional: 'bg-amber-100 text-amber-700',
  Site: 'bg-teal-100 text-teal-700',
};

const fmt = (s) => new Date(s).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

const initialForm = { date: '', name: '', holiday_type: 'National', region: '', is_optional: false };

export default function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    try {
      const res = await apiGet(`/hr/holidays?year=${year}`);
      setHolidays(res.holidays || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/hr/holidays', form);
      toast.success('Holiday added');
      setShowForm(false); setForm(initialForm); load();
    } catch (err) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete holiday?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/hr/holidays/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  // Group by month
  const byMonth = holidays.reduce((acc, h) => {
    const m = new Date(h.date).toLocaleString('en-IN', { month: 'long' });
    acc[m] = acc[m] || [];
    acc[m].push(h);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Holiday Calendar</h1>
          <p className="text-sm text-slate-600 mt-1">Manage national, regional and site-specific holidays.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="input text-sm">
            {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-holiday-btn">
            <Plus size={16} /> Add Holiday
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(byMonth).map(([month, items]) => (
          <div key={month} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-200">
              <div className="font-display font-bold text-sm text-[#0F172A]">{month} {year}</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {items.map(h => (
                <li key={h.id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50" data-testid={`holiday-row-${h.id}`}>
                  <div className="w-12 h-12 border border-slate-200 rounded-sm flex flex-col items-center justify-center shrink-0">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">{new Date(h.date).toLocaleString('en-IN', { weekday: 'short' })}</div>
                    <div className="font-display font-extrabold text-base text-[#1F7A6E]">{new Date(h.date).getDate()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm text-[#0F172A]">{h.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{fmt(h.date)}{h.region && ` · ${h.region}`}</div>
                  </div>
                  <span className={`tag ${TYPE_COLOR[h.holiday_type] || 'bg-slate-100 text-slate-700'}`}>{h.holiday_type}</span>
                  {h.is_optional && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">Optional</span>}
                  <button onClick={() => remove(h.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {!holidays.length && (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-sm text-slate-500">
            <Calendar size={28} className="mx-auto mb-2 text-slate-400" />
            No holidays defined for {year}.
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Add Holiday</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Date *</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input" required /></div>
                <div>
                  <label className="label">Type</label>
                  <select value={form.holiday_type} onChange={(e) => setForm({ ...form, holiday_type: e.target.value })} className="input">
                    <option>National</option><option>Regional</option><option>Site</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g., Diwali" required />
              </div>
              <div>
                <label className="label">Region (state / project)</label>
                <input type="text" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="input" placeholder="e.g., Maharashtra" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_optional} onChange={(e) => setForm({ ...form, is_optional: e.target.checked })} />
                Optional holiday
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
